import React from 'react';
import { Modal, Button, Space, Row, Col, Typography, Tag, Card, List, Empty } from 'antd';
import { RobotOutlined, PictureOutlined } from '@ant-design/icons';
import { SourceProcess, SourceCharacter, SourceChapter } from '../../../types';
import { formatDate } from '../../../utils';

const { Text } = Typography;

interface ProcessDetailModalProps {
  visible: boolean;
  onCancel: () => void;
  process: SourceProcess | null;
  storyCharacters: SourceCharacter[];
  chapters: SourceChapter[];
  onExtractCharacters: (process: SourceProcess) => void;
  onManageCharacterCheckpoints: (character: SourceCharacter) => void;
  loading: boolean;
}

const ProcessDetailModal: React.FC<ProcessDetailModalProps> = ({
  visible,
  onCancel,
  process,
  storyCharacters,
  chapters,
  onExtractCharacters,
  onManageCharacterCheckpoints,
  loading,
}) => {
  const getStatusTag = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return <Tag color="default">Draft</Tag>;
      case 'PROCESSING':
        return <Tag color="processing">Processing</Tag>;
      case 'COMPLETED':
        return <Tag color="success">Completed</Tag>;
      case 'FAILED':
        return <Tag color="error">Failed</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  if (!process) return null;

  const processCharacterIds = process.characterIds || [];
  const processCharacters = storyCharacters.filter(c => processCharacterIds.includes(c.id));
  const charactersWithAvatar = processCharacters.filter(c => !!c.avatarUrl).length;
  const charactersLocalized = processCharacters.filter(c => (c.localizedData as any)?.name).length;
  const allCharactersLocalized = processCharacters.length > 0 && charactersLocalized === processCharacters.length;

  return (
    <Modal
      title={`Process: ${process.name}`}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="close" onClick={onCancel}>
          Close
        </Button>
      ]}
      width={800}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Row gutter={24}>
          <Col span={8}>
            <Text strong>Status: </Text>
            {getStatusTag(process.status)}
          </Col>
          <Col span={8}>
            <Text strong>Form: </Text>
            <Tag color={process.videoForm === 'LONG' ? 'cyan' : 'purple'}>
              {process.videoForm === 'LONG' ? '16:9 Long' : '9:16 Short'}
            </Tag>
          </Col>
          <Col span={8}>
            <Text strong>Created: </Text>
            {formatDate(process.createdAt)}
          </Col>
        </Row>

        <Card
          size="small"
          title={
            <Space>
              <span>Characters</span>
              {processCharacterIds.length > 0 && (
                <>
                  <Tag color={charactersWithAvatar === processCharacterIds.length ? 'success' : 'warning'}>
                    Images: {charactersWithAvatar}/{processCharacterIds.length} done
                  </Tag>
                  <Tag color={allCharactersLocalized ? 'success' : 'warning'}>
                    Localized: {charactersLocalized}/{processCharacterIds.length} done
                  </Tag>
                </>
              )}
            </Space>
          }
          extra={
            <Button
              size="small"
              icon={<RobotOutlined />}
              onClick={() => onExtractCharacters(process)}
              loading={loading}
            >
              Re-extract
            </Button>
          }
        >
          {processCharacters.length === 0 ? (
            <Empty description="No characters" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <List
              grid={{ gutter: 12, column: 2 }}
              dataSource={processCharacters}
              renderItem={char => (
                <List.Item style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 8, border: '1px solid #f0f0f0', borderRadius: 8, width: '100%' }}>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        background: '#f0f0f0',
                        flexShrink: 0,
                        cursor: 'pointer'
                      }}
                      onClick={() => onManageCharacterCheckpoints(char)}
                    >
                      {char.avatarUrl ? (
                        <img src={char.avatarUrl} alt={char.name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <RobotOutlined style={{ fontSize: 20, color: '#bfbfbf' }} />
                        </div>
                      )}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {char.name}
                      </div>
                      {(char.localizedData as any)?.name && (
                        <div style={{ fontSize: 11, color: '#52c41a', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          Thay thế: {(char.localizedData as any).name}
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                        {char.role}
                      </div>
                    </div>
                    <Button
                      size="small"
                      icon={<PictureOutlined />}
                      onClick={() => onManageCharacterCheckpoints(char)}
                      type={char.avatarUrl ? "default" : "dashed"}
                      style={{
                        fontSize: '11px',
                        ...( !char.avatarUrl ? { color: '#1890ff', borderColor: '#1890ff' } : {})
                      }}
                    >
                      {char.avatarUrl ? 'Manage' : 'Create'}
                    </Button>
                  </div>
                </List.Item>
              )}
            />
          )}
        </Card>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Included Chapters:</Text>
          <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #f0f0f0', padding: '8px', borderRadius: '4px' }}>
            <List
              size="small"
              dataSource={process.chapterIds}
              renderItem={(chapterId) => {
                const chapter = chapters.find(c => c.id === chapterId);
                return (
                  <List.Item>
                    {chapter?.name || `Unknown Chapter (${chapterId})`}
                  </List.Item>
                );
              }}
            />
          </div>
        </div>

        {process.summary && (
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>AI Summary:</Text>
            <div style={{ backgroundColor: '#f9f9f9', padding: '12px', borderRadius: '4px', whiteSpace: 'pre-wrap', fontSize: '13px' }}>
              {process.summary}
            </div>
          </div>
        )}
      </Space>
    </Modal>
  );
};

export default ProcessDetailModal;
